import React, { useState, useRef, useEffect } from "react";
import { saveAs } from "file-saver";
import {
  Box, Flex, Text, Button, VStack,
  Heading, Icon, Input, Badge,
} from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import { FiDownload, FiCalendar } from "react-icons/fi";
import { TbReportAnalytics } from "react-icons/tb";

const parseDatePayload = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return { date: d, month: m, year: y };
};

const AUDIT_DATA_TRACKING_HEADERS = [
  "Start Date",
  "Start Time",
  "End Time",
  "Auditor Code",
  "Auditor Name",
  "Customer",
  "Store ID",
  "Outlet ID",
  "Store Name",
  "Store Type",
  "Store Class",
  "Total Images Captured",
];

const AUDIT_DATA_TRACKING_FIELDS = [
  "start_date",
  "start_time",
  "end_time",
  "auditor_code",
  "user_name",
  "customer",
  "store_id",
  "outlet_id",
  "store_name",
  "store_type",
  "store_cls",
  "total_images_captured",
];

const NO_WRAP_FIELDS = new Set(["start_time", "end_time"]);

const COLUMN_WIDTHS = {
  start_date:24,
  start_time: 24,
  end_time: 24,
  auditor_code: 14,
  user_name: 20,
  customer: 14,
  store_id: 16,
  outlet_id: 12,
  store_name: 28,
  store_type: 14,
  store_cls: 14,
  total_images_captured: 14,
};

export const handleAuditDataTrackingExport = async (from, to, setLoading) => {
  setLoading(true);
  const body = { from: parseDatePayload(from), to: parseDatePayload(to) };

  try {
    const res = await apiClient.post(
      `${API_CONFIG.ENDPOINTS.REPORT_CENTER.AUDIT_DATA_TRACKING}`,
      body
    );
    if (res.status !== 200) throw new Error("API failed: " + res.status);

    const list = Array.isArray(res.data) ? res.data : [res.data];

    const headerStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const borderStyle = {
      top:    { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left:   { style: "thin", color: { rgb: "000000" } },
      right:  { style: "thin", color: { rgb: "000000" } },
    };

    const dataStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
      font: { color: { rgb: "2D3748" }, sz: 11 },
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: borderStyle,
    };

    const dataStyleNoWrap = {
      fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
      font: { color: { rgb: "2D3748" }, sz: 11 },
      alignment: { horizontal: "left", vertical: "center", wrapText: false },
      border: borderStyle,
    };

    const wsData = [
      AUDIT_DATA_TRACKING_HEADERS.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...list.map((row) =>
        AUDIT_DATA_TRACKING_FIELDS.map((f) => ({
          v: row[f] !== undefined && row[f] !== null ? String(row[f]) : "",
          t: "s",
          s: NO_WRAP_FIELDS.has(f) ? dataStyleNoWrap : dataStyle,
        }))
      ),
    ];

    const XLSX = await import("xlsx-js-style");
    const ws    = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = AUDIT_DATA_TRACKING_FIELDS.map((f) => ({ wch: COLUMN_WIDTHS[f] || 22 }));
    ws["!rows"] = [{ hpt: 30 }, ...list.map(() => ({ hpt: 22 }))];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit Data Tracking");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Audit_Data_Tracking_Report_${from}_to_${to}.xlsx`
    );
  } catch (err) {
    console.error("Audit Data Tracking export error:", err);
    alert("Failed to generate Audit Data Tracking report: " + err.message);
  } finally {
    setLoading(false);
  }
};

export default function AuditDataTrackingReport() {
  const [loading, setLoading] = useState(false);

  const now   = new Date();
  const year  = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day   = String(now.getDate()).padStart(2, "0");

  const [fromDate, setFromDate] = useState(`${year}-${month}-01`);
  const [toDate,   setToDate]   = useState(`${year}-${month}-${day}`);

  const fromDateRef = useRef(fromDate);
  const toDateRef   = useRef(toDate);
  useEffect(() => { fromDateRef.current = fromDate; }, [fromDate]);
  useEffect(() => { toDateRef.current   = toDate;   }, [toDate]);

  const handleExport = () =>
    handleAuditDataTrackingExport(fromDateRef.current, toDateRef.current, setLoading);

  return (
    <Box bg="white" borderRadius="2xl" p={6} boxShadow="xl" border="1px solid" borderColor="gray.200">
      <Flex align="center" gap={3} mb={2}>
        <Icon as={TbReportAnalytics} boxSize={7} color="red.600" />
        <Heading size="md" color="gray.800">Audit Data Tracking Report</Heading>
        <Badge colorScheme="orange" variant="subtle" fontSize="xs" px={3} py={1} borderRadius="full">
          {AUDIT_DATA_TRACKING_HEADERS.length} columns
        </Badge>
      </Flex>
      <Text color="gray.500" fontSize="sm" mb={6}>
        Download auditor-wise store audit activity — session start/end time, auditor details,
        store identifiers, and total images captured — for the selected date range.
      </Text>

      <VStack spacing={5} align="stretch">
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">From Date</Text>
            <Box position="relative">
              <Icon as={FiCalendar} position="absolute" left="4" top="50%" transform="translateY(-50%)" color="red.500" zIndex={1} />
              <Input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                size="lg" pl="12" borderRadius="lg"
                border="2px solid" borderColor="gray.300" bg="white"
                _hover={{ borderColor: "red.400" }}
                _focus={{ borderColor: "red.500", boxShadow: "0 0 0 3px rgba(197,32,39,0.15)" }}
              />
            </Box>
          </Box>

          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">To Date</Text>
            <Box position="relative">
              <Icon as={FiCalendar} position="absolute" left="4" top="50%" transform="translateY(-50%)" color="red.500" zIndex={1} />
              <Input
                type="date" value={toDate} min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                size="lg" pl="12" borderRadius="lg"
                border="2px solid" borderColor="gray.300" bg="white"
                _hover={{ borderColor: "red.400" }}
                _focus={{ borderColor: "red.500", boxShadow: "0 0 0 3px rgba(197,32,39,0.15)" }}
              />
            </Box>
          </Box>
        </Flex>

        <Button
          onClick={handleExport}
          isLoading={loading}
          loadingText="Generating Report..."
          size="lg"
          bg="red.600"
          color="white"
          leftIcon={<FiDownload />}
          borderRadius="xl"
          fontWeight="bold"
          fontSize="md"
          _hover={{ bg: "red.700", transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(197,32,39,0.35)" }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.2s"
          w="100%"
        >
          Download Audit Data Tracking Report
        </Button>
      </VStack>
    </Box>
  );
}